import { SHA3 } from "sha3";
import sha256 from "js-sha256";

// M/D/YY, M/D/YYYY, MM/DD/YY, MM/DD/YYYY with slashes (2 or 4 digit year)
const SLASH_MDY = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;
// M-D-YYYY, MM-DD-YYYY with dashes (4 digit year only)
const DASH_MDY = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
// YYYY/M/D, YYYY-M-D (year-first, either separator)
const YMD = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/;

const isValidDateValue = (value) => {
  if (!value) return false;
  const trimmed = value.trim();

  let year, month, day;

  let match = trimmed.match(YMD);
  if (match) {
    year = parseInt(match[1], 10);
    month = parseInt(match[2], 10);
    day = parseInt(match[3], 10);
  } else {
    match = trimmed.match(SLASH_MDY) || trimmed.match(DASH_MDY);
    if (!match) return false;
    month = parseInt(match[1], 10);
    day = parseInt(match[2], 10);
    year = parseInt(match[3], 10);
    if (year < 100) year += 2000;
  }

  if (month < 1 || month > 12 || day < 1 || day > 31) return false;

  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

export default class DataFormatter {
  static generateSecureHash = (value, salt) => {
    if (!value || !salt || salt.length != 64) return;

    var hash = new SHA3(256).update(value + salt);

    return hash.digest("hex");
  };

  static formatDate = (value) => {
    if (!isValidDateValue(value)) {
      return null;
    }

    // JS dates are off by 1 day when yyyy-mm-dd
    // Additionally, m-d-yyyy is an invalid date format in Safari
    // Therefore, we convert all dates to use "/"" instead of "-""
    value = value.replace(/-/g, "/");

    // use US date format mm-dd-yyyy
    var dateString = new Date(value).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });

    // split formatted date
    var dateParts = dateString.split("/");

    if (dateParts.length === 3) {
      // convert to ISOString manually which will exclude timezone
      // ensure formatted date returned is yyyy-mm-dd
      return `${dateParts[2]}-${dateParts[0]}-${dateParts[1]}`;
    } else {
      // invalid date format
      return null;
    }
  };

  static removeLeadingZeros = (value) => {
    return value.replace(/^0+/, "");
  };

  static normalizeNdc = (value) => {
    if (!value) return value;

    const stripped = value.replace(/[-\s]/g, "");
    return stripped.padStart(11, "0");
  };

  static pharmacyEspId = (dateOfService, formattedRxNumber, rxNumber, ndc, serviceProviderId) => {
    const rx = formattedRxNumber || rxNumber || "";
    const raw = `${dateOfService ?? ""}${rx}${DataFormatter.normalizeNdc(ndc) ?? ""}${serviceProviderId ?? ""}`;

    return sha256.create().update(raw).hex();
  };

  static medicalEspId = (
    dateOfService,
    formattedClaimNumber,
    claimNumber,
    ndc,
    serviceProviderId
  ) => {
    const claimNum = formattedClaimNumber || claimNumber || "";
    const raw = `${dateOfService ?? ""}${claimNum}${DataFormatter.normalizeNdc(ndc) ?? ""}${serviceProviderId ?? ""}`;

    return sha256.create().update(raw).hex();
  };
}
